// src/pages/warehouse/WarehouseList.tsx

import React, { useState } from "react";
import { EDataTable } from "../../../components/EDataTable";
// Depo yönetimi için store'u içe aktar
// NOT: Bu store'un CustomerStore gibi CRUD fonksiyonlarını içerdiğini varsayıyoruz.
 
import { Column } from "primereact/column"; 
import { IconButton, TextButton } from "../../../components/ButtonComponents";
import { TableActions } from "../../../components/TableActions";
import { Edit, Trash2, Home } from "lucide-react"; // Home ikonu depo için uygun
import { WarehouseDialog } from "./WarehouseDialog"; // Depo ekleme/düzenleme dialogu
import { toast } from "react-toastify";
// Depo tipi (Modelin TypeScript karşılığı)
 
import { useConfirmModal } from "../../../hooks/useConfirmModal";
import { useApiErrorHandler } from "../../../hooks/useApiErrorHandler";
import { DataTableColumn, SimpleFilter } from "../../../types"; 
import { usePermission } from "../../../hooks/usePermission";
import { useWarehouseStore } from "../../../store/warehouse";
import { Warehouse } from "../../../types/warehouse";

export const WarehouseList = () => {
    // 1. Depo store hook'unu alıyoruz
    const store = useWarehouseStore();
    const { createItem, updateItem, deleteItem } = store; 
    const { handleApiError } = useApiErrorHandler();
    const { canCreate, canUpdate, canDelete } = usePermission('warehouseModule');
    
    // --- STATE'LER ---
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [warehouseToEdit, setWarehouseToEdit] = useState<Warehouse | null>(null);

    // --- ACTION HANDLERS ---
    const handleNew = () => {
        setWarehouseToEdit(null);
        setIsModalOpen(true);
    };

    const handleEdit = (warehouse: Warehouse) => {
        setWarehouseToEdit(warehouse);
        setIsModalOpen(true);
    };

    const { confirm, modal: confirmModal } = useConfirmModal();

    const handleDeleteClick = async (warehouse: Warehouse) => {
        const ok = await confirm(`${warehouse.name} adlı depoyu silmek istiyor musun? Stok hareketlerini kontrol et!`);

        if (ok) {
            try {
                await deleteItem(warehouse.id);
                toast.success("Depo silindi");
            } catch (error) {
                handleApiError(error); 
            }
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setWarehouseToEdit(null);
    };

    // Warehouse tipinde 'id' hariç veriyi alır
    const handleSaveWarehouse = async (warehouseData: Omit<Warehouse, "id">) => { 
        const cleanedData: any = { ...warehouseData };
 
        try {
            if (warehouseToEdit) {
                // Düzenleme
                await updateItem(warehouseToEdit.id, cleanedData);
                toast.success(`Depo Güncellendi: ${cleanedData.name}`);
            } else {
                // Oluşturma
                await createItem(cleanedData);
                toast.success(`Yeni Depo Eklendi: ${cleanedData.name}`);
            }
            
        } catch (error) {
            handleApiError(error); 
        }
        handleCloseModal(); 
    };


    // --- DataTable body templates ---
    
    // Depo aktiflik durumu
    const activeBodyTemplate = (warehouse: Warehouse) => (
        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${warehouse.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
             {warehouse.active ? "Aktif" : "Pasif"}
        </span>
    );
    
    // Adres sadece ilk 30 karakter + ...
    const addressBodyTemplate = (warehouse: Warehouse) => (
        <span title={warehouse.address || ""}>
             {warehouse.address && warehouse.address.length > 30 ? warehouse.address.substring(0, 30) + "..." : warehouse.address || "-"}
        </span>
    );

    const actionBodyTemplate = (warehouse: Warehouse) => (
        <TableActions
            actions={[
                {
                    icon: Edit,
                    colorClass: "text-blue-600 hover:bg-blue-50",
                    title: "Düzenle",
                    onClick: () => handleEdit(warehouse),
                    show: canUpdate
                },
                {
                    icon: Trash2,
                    colorClass: "text-red-500 hover:bg-red-50",
                    title: "Sil",
                    onClick: () => handleDeleteClick(warehouse),
                    show: !!warehouse.active && canDelete
                }
            ]}
        />
    );

    const warehouseColumns: DataTableColumn<Warehouse>[] = [
        { field: "id", header: "ID", width: "80px" },
        { 
            field: "name", 
            header: "Depo Adı", 
            width: "250px", 
            sortable: true, 
        },
        { 
            field: "address", 
            header: "Adres", 
            width: "350px", 
            body: addressBodyTemplate,
            sortable: false, 
        },
        { 
            field: "active", 
            header: "Durum", 
            width: "120px", 
            align: "center",
            body: activeBodyTemplate,
            sortable: true,
        },
        { 
            field: "actions", 
            header: "İşlemler", 
            width: "120px", 
            align: "center",
            body: actionBodyTemplate 
        },
    ];

    return (
        <div className="w-full">
            {confirmModal}
            <EDataTable<Warehouse> 
            tableKey="warehouse_list"
                useStore={() => store} 
                dataKey="id"
                title="Depo Listesi"
                placeholder="Depo Adı Ara"
                // Global arama sadece 'name' alanı üzerinden yapılabilir
                searchFields={[
                    { field: "name", operator: "contains" as SimpleFilter["operator"] },
                ]}
                // Aksiyon butonunu tanımla
                actions={
                    canCreate ? (<TextButton onClick={handleNew} className="btn-new">
                         Yeni Depo Ekle
                    </TextButton>) : undefined
                }
                // Sütunları tanımla
                columns={warehouseColumns}
            />

            {/* Depo Ekleme/Düzenleme Dialogu */}
            <WarehouseDialog
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                warehouse={warehouseToEdit}
                onSave={handleSaveWarehouse}
            />
        </div>
    );
};